import { useState, useCallback, useEffect, useMemo } from "react";
import { useAuth } from "../../context/AuthProvider";
import { BASE_URL } from "../../apiConfig"; 
import { toast } from 'sonner'; 
import {Link} from "react-router-dom";

export default function UpdateFournisseurForm({ fournisseur }) {
  const { token } = useAuth();
  const [formData, setFormData] = useState(fournisseur || {
    id: null,
    name: "",
    name_personnel: "",
    email: "",
    phone: "",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({}); // Renamed for clarity from 'errors'
  const [apiError, setApiError] = useState("");

  useEffect(() => {
    if (fournisseur) {
      setFormData(fournisseur);
      setValidationErrors({}); // Clear errors when a new fournisseur is loaded
      setApiError("");
    }
  }, [fournisseur]); // Dependency on fournisseur ensures it re-runs when the prop changes

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear the specific validation error for the field being changed
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: undefined })); // Use undefined to truly remove the key
    }
    setApiError(""); // Clear general API error on user interaction
  }, [validationErrors]); // Dependency on validationErrors to correctly clear

  // Form Submission Handler
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setValidationErrors({}); // Clear previous backend validation errors
    setApiError(""); // Clear previous general API errors

    if (!token) {
      toast.error("Authentification requise. Veuillez vous connecter."); // French: Authentication required. Please log in.
      return;
    }

    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = "Le nom du fournisseur est requis.";
    }
    if (!formData.name_personnel.trim()) {
      newErrors.name_personnel = "Le nom du personnel est requis.";
    }
    if (!formData.email.trim()) {
      newErrors.email = "L'adresse e-mail est requise.";
    } else if (!/\S+.\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Veuillez entrer une adresse e-mail valide.";
    }
    if (!formData.phone.trim()) {
      newErrors.phone = "Le numéro de téléphone est requis.";
    } else if (!/^\+?[0-9\s-()]{7,20}$/.test(formData.phone)) {
      newErrors.phone = "Veuillez entrer un numéro de téléphone valide.";
    }

    if (Object.keys(newErrors).length > 0) {
      setValidationErrors(newErrors);
      toast.error("Veuillez corriger les erreurs dans le formulaire.");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${BASE_URL}/api/fournisseurs/${formData.id}`, {
        method: "PUT", 
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (!response.ok) {
        if (response.status === 422 && result.errors) {
          setValidationErrors(result.errors);
          toast.error(result.message || "Veuillez corriger les champs mis en évidence."); // French: Please correct the highlighted fields.
        } else {
          const errorMessage = result.message || `Erreur: ${response.statusText}`; // French: Error
          setApiError(errorMessage);
          toast.error(errorMessage);
        }
        return; // Stop execution if there's an error
      }

      // Success:
      toast.success("Fournisseur mis à jour avec succès !"); // French: Supplier updated successfully!
      setFormData(result); // Assuming the API returns the updated fournisseur
      setValidationErrors({}); // Ensure all error states are cleared on success
      setApiError("");

    } catch (error) {
      console.error("Erreur réseau ou inattendue lors de la mise à jour du fournisseur:", error); // French: Network or unexpected error during supplier update
      setApiError("Une erreur réseau inattendue est survenue. Veuillez vérifier votre connexion."); // French: An unexpected network error occurred. Please check your connection.
      toast.error("Une erreur inattendue est survenue. Veuillez réessayer."); // French: An unexpected error occurred. Please try again.
    } finally {
      setIsLoading(false);
    }
  }, [formData, token]); // Dependencies: formData (for body), token (for auth)

  const previewData = useMemo(() => {
    return {
      displayName: formData.name || "Nom du Fournisseur", // Default text
      displayPersonnel: formData.name_personnel || "Personne de Contact",
      displayEmail: formData.email || "Email",
      displayPhone: formData.phone || "Téléphone",
    };
  }, [formData]); // Recompute preview data when formData changes

  return (
    <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
      {/* API Error Display */}
      {apiError && (
        <div className="mb-6 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-center font-medium" role="alert">
          <p>{apiError}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="p-8">
        {/* Fournisseur Name */}
        <div className="mb-8">
          <label htmlFor="name" className="block font-semibold text-gray-900 mb-3 text-sm">Nom du Fournisseur <span className="text-red-500">*</span></label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name || ''} // Ensure value is never null/undefined for controlled input
            onChange={handleInputChange}
            placeholder="Entrez le nom du fournisseur..."
            className={`w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
              validationErrors.name
                ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
            }`}
            aria-invalid={!!validationErrors.name} // ARIA for accessibility
            aria-describedby={validationErrors.name ? "name-error" : undefined}
          />
          {validationErrors.name && <p id="name-error" className="mt-2 text-red-600 text-xs font-medium">{validationErrors.name[0]}</p>}
        </div>

        {/* Contact Person Name */}
        <div className="mb-8">
          <label htmlFor="name_personnel" className="block font-semibold text-gray-900 mb-3 text-sm">Nom du Contact <span className="text-red-500">*</span></label>
          <input
            type="text"
            id="name_personnel"
            name="name_personnel"
            value={formData.name_personnel || ''}
            onChange={handleInputChange}
            placeholder="Entrez le nom de la personne de contact..."
            className={`w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
              validationErrors.name_personnel
                ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
            }`}
            aria-invalid={!!validationErrors.name_personnel}
            aria-describedby={validationErrors.name_personnel ? "name-personnel-error" : undefined}
          />
          {validationErrors.name_personnel && <p id="name-personnel-error" className="mt-2 text-red-600 text-xs font-medium">{validationErrors.name_personnel[0]}</p>}
        </div>

        {/* Email */}
        <div className="mb-8">
          <label htmlFor="email" className="block font-semibold text-gray-900 mb-3 text-sm">Email <span className="text-red-500">*</span></label>
          <input
            type="email" // Use type="email" for better mobile keyboard and basic validation
            id="email"
            name="email"
            value={formData.email || ''}
            onChange={handleInputChange}
            placeholder="Entrez l'adresse e-mail..."
            className={`w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
              validationErrors.email
                ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
            }`}
            aria-invalid={!!validationErrors.email}
            aria-describedby={validationErrors.email ? "email-error" : undefined}
          />
          {validationErrors.email && <p id="email-error" className="mt-2 text-red-600 text-xs font-medium">{validationErrors.email[0]}</p>}
        </div>

        {/* Phone */}
        <div className="mb-8">
          <label htmlFor="phone" className="block font-semibold text-gray-900 mb-3 text-sm">Téléphone <span className="text-red-500">*</span></label>
          <input
            type="tel" // Use type="tel" for better mobile keyboard
            id="phone"
            name="phone"
            value={formData.phone || ''}
            onChange={handleInputChange}
            placeholder="Entrez le numéro de téléphone..."
            className={`w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
              validationErrors.phone
                ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
            }`}
            aria-invalid={!!validationErrors.phone}
            aria-describedby={validationErrors.phone ? "phone-error" : undefined}
          />
          {validationErrors.phone && <p id="phone-error" className="mt-2 text-red-600 text-xs font-medium">{validationErrors.phone[0]}</p>}
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-200 mt-10">
          <Link to="/fournisseurs" className="block text-center flex-1 py-3 px-4 cursor-pointer text-sm border-2 border-gray-200 text-gray-700 font-semibold rounded-2xl hover:border-gray-300 hover:bg-gray-50 transition-all duration-200">
            <button
              type="button"
              disabled={isLoading}
              className="cursor-pointer"
            >
              Annuler  
            </button>
          </Link> 
          <button
            type="submit"
            disabled={isLoading}
            className={`flex-1 py-3 cursor-pointer px-4 text-sm font-semibold rounded-2xl flex items-center justify-center gap-2 transition-all duration-200 ${
              isLoading
                ? "bg-gray-400 text-white cursor-not-allowed"
                : "bg-[var(--color-primary-500)] hover:bg-[var(--color-primary-400)] text-white shadow-lg hover:shadow-xl transform hover:scale-[1.01]" // Subtle scale on hover
            }`}
          >
            {isLoading ? (
              <>
                <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Enregistrement...
              </>
            ) : (
              <>
                Enregistrer
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /> {/* Checkmark icon */}
                </svg>
              </>
            )}
          </button>
        </div>

        {/* Preview Section */}
        {(formData.name || formData.name_personnel || formData.email || formData.phone) && (
          <div className="mt-10 p-6 bg-gray-50 rounded-2xl border border-gray-200">
            <h3 className="font-semibold text-gray-900 mb-4">Aperçu en direct</h3>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[var(--color-primary-50)] rounded-full flex items-center justify-center text-[var(--color-primary-500)] flex-shrink-0">
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 21v-2a4 4 0 00-4-4H9a4 4 0 00-4 4v2m7-12a4 4 0 100-8 4 4 0 000 8zm7 0a4 4 0 100-8 4 4 0 000 8zm-7 0a4 4 0 100-8 4 4 0 000 8z"
                  />
                </svg>
              </div>
              <div>
                <div className="font-bold text-sm text-gray-900">{previewData.displayName}</div>
                <div className="text-base text-xs text-gray-600 mt-1">
                  Contact: {previewData.displayPersonnel}
                </div>
                <div className="text-base text-xs text-gray-600">
                  Email: {previewData.displayEmail}
                </div>
                <div className="text-base text-xs text-gray-600">
                  Téléphone: {previewData.displayPhone}
                </div>
              </div>
            </div>
          </div>
        )}
      </form>
    </div>
  );
}
